import Vue from 'vue';
// Gems-specific store module
export class GemsStore {
    state = Vue.observable({
        sessionGems: null,
        loading: false,
        error: null,
        lastUpdated: null,
        newGems: [],
        flashingGems: {}
    });
    // Getters
    get sessionGems() {
        return this.state.sessionGems;
    }
    get loading() {
        return this.state.loading;
    }
    get error() {
        return this.state.error;
    }
    get lastUpdated() {
        return this.state.lastUpdated;
    }
    get newGems() {
        return this.state.newGems;
    }
    get flashingGems() {
        return this.state.flashingGems;
    }
    get hasGems() {
        return this.state.sessionGems !== null && Object.keys(this.state.sessionGems).length > 0;
    }
    // Actions
    setLoading(loading) {
        this.state.loading = loading;
    }
    setError(error) {
        this.state.error = error;
    }
    setSessionGems(gems) {
        this.state.sessionGems = gems;
        this.state.lastUpdated = new Date();
        this.state.error = null;
    }
    /**
     * Add new gems to the session, handling deduplication and visual feedback
     * @param newGems - The new gems to add
     */
    addGems(newGems) {
        console.log('[GemsStore] ====== STARTING addGems() ======');
        console.log('[GemsStore] Adding new gems:', newGems);
        console.log('[DEBUG][GemsStore] NEW GEMS STRUCTURE:', JSON.stringify(newGems, null, 2));
        console.log('[DEBUG][GemsStore] NEW GEMS TYPES:', Object.keys(newGems));
        console.log('[🔥 CRITICAL DEBUG] CURRENT sessionGems REFERENCE:', this.state.sessionGems);
        console.log('[🔥 CRITICAL DEBUG] Is sessionGems null?', this.state.sessionGems === null);
        console.log('[🔥 CRITICAL DEBUG] Is sessionGems undefined?', this.state.sessionGems === undefined);
        console.log('[DEBUG][GemsStore] CURRENT STATE before adding:', JSON.stringify(this.state.sessionGems, null, 2));
        console.log('[DEBUG][GemsStore] CURRENT TYPES before adding:', this.state.sessionGems ? Object.keys(this.state.sessionGems) : 'none');
        if (!this.state.sessionGems) {
            console.log('[DEBUG][GemsStore] Initializing EMPTY sessionGems object');
            this.state.sessionGems = {};
        }
        else {
            console.log('[🔥 CRITICAL] sessionGems ALREADY EXISTS with types:', Object.keys(this.state.sessionGems));
        }
        const addedGems = [];
        const newGemTypes = [];
        // Merge new gems with existing ones
        Object.keys(newGems).forEach(gemType => {
            console.log('[DEBUG][GemsStore] *** Processing gem type:', gemType, '***');
            const isNewType = !this.state.sessionGems[gemType];
            console.log('[DEBUG][GemsStore] Is this a NEW TYPE?', isNewType);
            if (isNewType) {
                console.log('[DEBUG][GemsStore] ✨ Creating NEW ARRAY for ENTIRELY NEW gem type:', gemType);
                this.state.sessionGems[gemType] = [];
                newGemTypes.push(gemType);
                console.log('[DEBUG][GemsStore] ✨ Added', gemType, 'to newGemTypes array');
            }
            const existingGems = this.state.sessionGems[gemType];
            const newGemsOfType = newGems[gemType];
            console.log('[DEBUG][GemsStore] Existing gems for type', gemType, ':', existingGems?.length || 0);
            console.log('[DEBUG][GemsStore] New gems for type', gemType, ':', newGemsOfType?.length || 0);
            console.log('[DEBUG][GemsStore] New gems content:', newGemsOfType);
            // Add only gems that don't already exist (basic deduplication by quote)
            newGemsOfType?.forEach((newGem, index) => {
                console.log('[DEBUG][GemsStore] --- Checking gem', index, ':', newGem.quote || newGem.text);
                const exists = existingGems.some(existing => (existing.quote || existing.text) === (newGem.quote || newGem.text));
                console.log('[DEBUG][GemsStore] --- Does gem already exist?', exists);
                if (!exists) {
                    console.log('[DEBUG][GemsStore] ✅ ADDING NEW GEM:', newGem.quote || newGem.text, 'to type:', gemType);
                    existingGems.push(newGem);
                    addedGems.push({ ...newGem, type: gemType }); // Add type to track which type it belongs to
                    // Add to flashing object for visual feedback
                    const gemId = `${gemType}-${newGem.quote || newGem.text}`;
                    console.log('[DEBUG][GemsStore] ✅ ADDING TO FLASHING OBJECT:', gemId);
                    Vue.set(this.state.flashingGems, gemId, true);
                    // Remove from flashing object after animation duration
                    setTimeout(() => {
                        console.log('[DEBUG][GemsStore] Removing from flashing object:', gemId);
                        Vue.delete(this.state.flashingGems, gemId);
                    }, 800); // 800ms flash duration to match animation
                }
                else {
                    console.log('[DEBUG][GemsStore] ⏭️ Gem already exists, skipping:', newGem.quote || newGem.text);
                }
            });
            console.log('[DEBUG][GemsStore] *** FINISHED processing type:', gemType, '***');
            console.log('[DEBUG][GemsStore] *** Final gems count for', gemType, ':', existingGems.length);
        });
        // For entirely new gem types, add a special flash marker for the whole card
        newGemTypes.forEach(newType => {
            const typeFlashId = `TYPE-${newType}`;
            console.log('[DEBUG][GemsStore] ✨ ADDING TYPE FLASH for new gem type:', typeFlashId);
            Vue.set(this.state.flashingGems, typeFlashId, true);
            // Remove type flash after animation duration
            setTimeout(() => {
                console.log('[DEBUG][GemsStore] Removing TYPE flash for:', typeFlashId);
                Vue.delete(this.state.flashingGems, typeFlashId);
            }, 800);
        });
        // Update tracking
        this.state.newGems = addedGems;
        this.state.lastUpdated = new Date();
        this.state.error = null;
        console.log('[GemsStore] ====== FINAL RESULTS ======');
        console.log('[GemsStore] Added gems count:', addedGems.length);
        console.log('[GemsStore] New gem types count:', newGemTypes.length);
        console.log('[GemsStore] New gem types list:', newGemTypes);
        console.log('[DEBUG][GemsStore] FINAL STATE after adding:', JSON.stringify(this.state.sessionGems, null, 2));
        console.log('[DEBUG][GemsStore] FINAL TYPES after adding:', this.state.sessionGems ? Object.keys(this.state.sessionGems) : 'none');
        // Trigger a custom event for UI components to react to new gems
        if (addedGems.length > 0 || newGemTypes.length > 0) {
            console.log('[DEBUG][GemsStore] 🎉 DISPATCHING gems-updated event with', addedGems.length, 'new gems and', newGemTypes.length, 'new types');
            window.dispatchEvent(new CustomEvent('gems-updated', {
                detail: {
                    newGems: addedGems,
                    newTypes: newGemTypes,
                    timestamp: new Date().toISOString(),
                    count: addedGems.length
                }
            }));
        }
        else {
            console.log('[DEBUG][GemsStore] ❌ NO NEW GEMS OR TYPES to add, not dispatching event');
        }
        console.log('[GemsStore] ====== FINISHED addGems() ======');
    }
    /**
     * Check if a gem is currently flashing
     * @param gemType - The gem type
     * @param gemText - The gem text
     * @returns True if the gem is currently flashing
     */
    isGemFlashing(gemType, gemText) {
        const gemId = `${gemType}-${gemText}`;
        return !!this.state.flashingGems[gemId];
    }
    /**
     * Check if an entire gem type is currently flashing (new type)
     * @param gemType - The gem type
     * @returns True if the type is currently flashing
     */
    isGemTypeFlashing(gemType) {
        const typeFlashId = `TYPE-${gemType}`;
        return !!this.state.flashingGems[typeFlashId];
    }
    /**
     * Clear new gems list (called after UI has processed them)
     */
    clearNewGems() {
        this.state.newGems = [];
    }
    /**
     * Clear all gems data
     */
    clearAll() {
        this.state.sessionGems = null;
        this.state.loading = false;
        this.state.error = null;
        this.state.lastUpdated = null;
        this.state.newGems = [];
        this.state.flashingGems = {};
    }
    /**
     * Force clear the flashing state for a specific gem
     * @param gemType - The gem type
     * @param gemText - The gem text
     */
    clearFlashing(gemType, gemText) {
        const gemId = `${gemType}-${gemText}`;
        Vue.delete(this.state.flashingGems, gemId);
    }
}
// Export singleton instance
export const gemsStore = new GemsStore();
